import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Lock, Camera, Save, Package, Heart, LogOut, Edit2 } from 'lucide-react';
import Header from './components/Header20';
import Footer from './components/Footer20';
import Button from './components/Button';

const Profile = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('profile');
    const [isEditing, setIsEditing] = useState(false);
    const [profileData, setProfileData] = useState({
        fullName: 'John Doe',
        email: 'john.doe@example.com',
        phone: '+91 98765 43210',
        avatar: 'https://ui-avatars.com/api/?name=John+Doe&size=200&background=f97316&color=fff',
        address: {
            street: '123 Creative Lane',
            city: 'Mumbai',
            state: 'Maharashtra',
            pincode: '400001',
        },
        bio: 'Creative enthusiast and art lover. Always exploring new designs and artistic expressions.',
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setProfileData({
                ...profileData,
                [parent]: {
                    ...profileData[parent],
                    [child]: value,
                },
            });
        } else {
            setProfileData({ ...profileData, [name]: value });
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData({ ...passwordData, [name]: value });
    };

    const handleSaveProfile = (e) => {
        e.preventDefault();
        console.log('Profile updated:', profileData);
        setIsEditing(false);
        // Show success message
    };

    const handleChangePassword = (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            alert('Passwords do not match!');
            return;
        }
        console.log('Password changed');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        // Show success message
    };

    const handleLogout = () => {
        // Add logout logic
        navigate('/template20/login');
    };

    const tabs = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'orders', label: 'Orders', icon: Package },
        { id: 'wishlist', label: 'Wishlist', icon: Heart },
        { id: 'security', label: 'Security', icon: Lock },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
            <Header showBackButton={true} cartCount={3} />

            <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
                <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-8">
                    My Account
                </h1>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl shadow-lg p-6">
                            {/* Profile Picture */}
                            <div className="text-center mb-6">
                                <div className="relative inline-block">
                                    <img
                                        src={profileData.avatar}
                                        alt={profileData.fullName}
                                        className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-orange-200"
                                    />
                                    <button className="absolute bottom-4 right-0 p-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full hover:scale-110 transition-transform">
                                        <Camera size={16} className="text-white" />
                                    </button>
                                </div>
                                <h3 className="font-bold text-gray-900">{profileData.fullName}</h3>
                                <p className="text-sm text-gray-600">{profileData.email}</p>
                            </div>

                            {/* Navigation Tabs */}
                            <div className="space-y-2">
                                {tabs.map(tab => {
                                    const Icon = tab.icon;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                                                activeTab === tab.id
                                                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg'
                                                    : 'text-gray-700 hover:bg-orange-50'
                                            }`}
                                        >
                                            <Icon size={20} />
                                            <span className="font-medium">{tab.label}</span>
                                        </button>
                                    );
                                })}
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all"
                                >
                                    <LogOut size={20} />
                                    <span className="font-medium">Logout</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="lg:col-span-3">
                        {/* Profile Tab */}
                        {activeTab === 'profile' && (
                            <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-8">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                        Profile Information
                                    </h2>
                                    <Button
                                        variant={isEditing ? 'secondary' : 'primary'}
                                        size="sm"
                                        onClick={() => setIsEditing(!isEditing)}
                                    >
                                        <Edit2 size={16} className="inline mr-2" />
                                        {isEditing ? 'Cancel' : 'Edit'}
                                    </Button>
                                </div>

                                <form onSubmit={handleSaveProfile}>
                                    <div className="space-y-6">
                                        {/* Personal Information */}
                                        <div>
                                            <h3 className="font-semibold text-lg text-gray-900 mb-4">Personal Details</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Full Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="fullName"
                                                        value={profileData.fullName}
                                                        onChange={handleInputChange}
                                                        disabled={!isEditing}
                                                        className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Email
                                                    </label>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        value={profileData.email}
                                                        onChange={handleInputChange}
                                                        disabled={!isEditing}
                                                        className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Phone
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        value={profileData.phone}
                                                        onChange={handleInputChange}
                                                        disabled={!isEditing}
                                                        className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Bio */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Bio
                                            </label>
                                            <textarea
                                                name="bio"
                                                value={profileData.bio}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                                rows="3"
                                                className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50 resize-none"
                                            />
                                        </div>

                                        {/* Address */}
                                        <div>
                                            <h3 className="font-semibold text-lg text-gray-900 mb-4">Address</h3>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Street Address
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="address.street"
                                                        value={profileData.address.street}
                                                        onChange={handleInputChange}
                                                        disabled={!isEditing}
                                                        className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                            City
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="address.city"
                                                            value={profileData.address.city}
                                                            onChange={handleInputChange}
                                                            disabled={!isEditing}
                                                            className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                            State
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="address.state"
                                                            value={profileData.address.state}
                                                            onChange={handleInputChange}
                                                            disabled={!isEditing}
                                                            className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                            PIN Code
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="address.pincode"
                                                            value={profileData.address.pincode}
                                                            onChange={handleInputChange}
                                                            disabled={!isEditing}
                                                            className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {isEditing && (
                                            <Button type="submit" variant="primary" size="lg">
                                                <Save size={20} className="inline mr-2" />
                                                Save Changes
                                            </Button>
                                        )}
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Orders Tab */}
                        {activeTab === 'orders' && (
                            <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-8">
                                <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                    Recent Orders
                                </h2>
                                <div className="text-center py-12">
                                    <Package size={64} className="text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-600 mb-6">View all your orders in one place</p>
                                    <Button variant="primary" onClick={() => navigate('/template20/orders')}>
                                        View All Orders
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Wishlist Tab */}
                        {activeTab === 'wishlist' && (
                            <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-8">
                                <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                    My Wishlist
                                </h2>
                                <div className="text-center py-12">
                                    <Heart size={64} className="text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-600 mb-6">Your wishlist is empty</p>
                                    <Button variant="primary" onClick={() => navigate('/template20/shop')}>
                                        Start Shopping
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Security Tab */}
                        {activeTab === 'security' && (
                            <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-8">
                                <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                    Security Settings
                                </h2>
                                <form onSubmit={handleChangePassword}>
                                    <div className="space-y-6">
                                        <div>
                                            <h3 className="font-semibold text-lg text-gray-900 mb-4">Change Password</h3>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Current Password
                                                    </label>
                                                    <input
                                                        type="password"
                                                        name="currentPassword"
                                                        value={passwordData.currentPassword}
                                                        onChange={handlePasswordChange}
                                                        className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        New Password
                                                    </label>
                                                    <input
                                                        type="password"
                                                        name="newPassword"
                                                        value={passwordData.newPassword}
                                                        onChange={handlePasswordChange}
                                                        minLength="8"
                                                        className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Confirm New Password
                                                    </label>
                                                    <input
                                                        type="password"
                                                        name="confirmPassword"
                                                        value={passwordData.confirmPassword}
                                                        onChange={handlePasswordChange}
                                                        minLength="8"
                                                        className="w-full px-4 py-3 border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <Button type="submit" variant="primary" size="lg">
                                            <Lock size={20} className="inline mr-2" />
                                            Update Password
                                        </Button>
                                    </div>
                                </form>

                                <div className="mt-8 pt-8 border-t border-gray-200">
                                    <h3 className="font-semibold text-lg text-gray-900 mb-4">Two-Factor Authentication</h3>
                                    <p className="text-gray-600 mb-4">
                                        Add an extra layer of security to your account
                                    </p>
                                    <Button variant="outline">Enable 2FA</Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Profile;
